/**
 * SAML SSO Configuration Manager
 * Admin page for configuring SAML 2.0 SSO with LANES LA or other IdPs.
 */

import React, { useState, useEffect } from 'react';
import { Shield, Upload, Download, RefreshCw, CheckCircle, AlertCircle, Trash2, Plus, X } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { apiClient } from '@/services/apiClient';

interface SAMLConfig {
  configured: boolean;
  enabled?: boolean;
  sp_entity_id?: string;
  sp_acs_url?: string;
  idp_entity_id?: string;
  idp_sso_url?: string;
  idp_slo_url?: string;
  jit_provisioning_enabled?: boolean;
  default_role_id?: number;
  force_sso?: boolean;
  want_assertions_signed?: boolean;
  want_response_signed?: boolean;
  attribute_mapping?: Record<string, string>;
  role_mapping?: Record<string, string>;
  email_domains?: string[];
}

export function SAMLSSOManager() {
  const [config, setConfig] = useState<SAMLConfig>({ configured: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ status: string; checks?: any[] } | null>(null);
  const [testing, setTesting] = useState(false);

  // Form state
  const [idpEntityId, setIdpEntityId] = useState('');
  const [idpSsoUrl, setIdpSsoUrl] = useState('');
  const [idpSloUrl, setIdpSloUrl] = useState('');
  const [idpCert, setIdpCert] = useState('');
  const [jitEnabled, setJitEnabled] = useState(false);
  const [forceSso, setForceSso] = useState(false);
  const [wantAssertionsSigned, setWantAssertionsSigned] = useState(true);
  const [wantResponseSigned, setWantResponseSigned] = useState(true);
  const [emailDomains, setEmailDomains] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [attrEmail, setAttrEmail] = useState('urn:oid:0.9.2342.19200300.100.1.3');
  const [attrFirstName, setAttrFirstName] = useState('urn:oid:2.5.4.42');
  const [attrLastName, setAttrLastName] = useState('urn:oid:2.5.4.4');
  const [attrRoles, setAttrRoles] = useState('urn:oid:1.3.6.1.4.1.5923.1.1.1.7');

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get('/api/saml/config');
      const data = resp.data;
      setConfig(data);
      if (data.configured) {
        setIdpEntityId(data.idp_entity_id || '');
        setIdpSsoUrl(data.idp_sso_url || '');
        setIdpSloUrl(data.idp_slo_url || '');
        setJitEnabled(data.jit_provisioning_enabled || false);
        setForceSso(data.force_sso || false);
        setWantAssertionsSigned(data.want_assertions_signed ?? true);
        setWantResponseSigned(data.want_response_signed ?? true);
        setEmailDomains(data.email_domains || []);
        if (data.attribute_mapping) {
          setAttrEmail(data.attribute_mapping.email || attrEmail);
          setAttrFirstName(data.attribute_mapping.first_name || attrFirstName);
          setAttrLastName(data.attribute_mapping.last_name || attrLastName);
          setAttrRoles(data.attribute_mapping.roles || attrRoles);
        }
      }
    } catch (e) {
      console.error('Failed to load SAML config', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/saml/config', {
        idp_entity_id: idpEntityId,
        idp_sso_url: idpSsoUrl,
        idp_slo_url: idpSloUrl,
        idp_cert: idpCert || undefined,
        jit_provisioning_enabled: jitEnabled,
        force_sso: forceSso,
        want_assertions_signed: wantAssertionsSigned,
        want_response_signed: wantResponseSigned,
        email_domains: emailDomains,
        attribute_mapping: {
          email: attrEmail,
          first_name: attrFirstName,
          last_name: attrLastName,
          roles: attrRoles,
        },
      });
      await fetchConfig();
    } catch (e) {
      console.error('Failed to save SAML config', e);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await apiClient.post('/api/saml/config/test');
      setTestResult(resp.data);
    } catch (e) {
      setTestResult({ status: 'error', checks: [{ check: 'Connection', passed: false, error: 'Request failed' }] });
    } finally {
      setTesting(false);
    }
  };

  const handleDisable = async () => {
    try {
      await apiClient.delete('/api/saml/config');
      await fetchConfig();
    } catch (e) {
      console.error('Failed to disable SSO', e);
    }
  };

  const handleUploadMetadata = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      const resp = await apiClient.post('/api/saml/config/upload-idp-metadata', text, {
        headers: { 'Content-Type': 'application/xml' },
      });
      const parsed = resp.data.parsed;
      if (parsed) {
        setIdpEntityId(parsed.idp_entity_id || '');
        setIdpSsoUrl(parsed.idp_sso_url || '');
        setIdpSloUrl(parsed.idp_slo_url || '');
        setIdpCert(parsed.idp_cert || '');
      }
    } catch (err) {
      console.error('Failed to parse IdP metadata', err);
    }
  };

  const addDomain = () => {
    const d = newDomain.trim().toLowerCase();
    if (d && !emailDomains.includes(d)) {
      setEmailDomains([...emailDomains, d]);
      setNewDomain('');
    }
  };

  const removeDomain = (domain: string) => {
    setEmailDomains(emailDomains.filter(d => d !== domain));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              <div>
                <CardTitle>SAML SSO Configuration</CardTitle>
                <CardDescription>Configure Single Sign-On with your Identity Provider</CardDescription>
              </div>
            </div>
            <Badge variant={config.enabled ? "default" : "secondary"}>
              {config.enabled ? "Enabled" : "Disabled"}
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* SP Metadata */}
      {config.configured && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Service Provider Details</CardTitle>
            <CardDescription>Share these with your IdP administrator</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">SP Entity ID</Label>
              <Input value={config.sp_entity_id || ''} readOnly className="font-mono text-sm bg-muted" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">ACS URL</Label>
              <Input value={config.sp_acs_url || ''} readOnly className="font-mono text-sm bg-muted" />
            </div>
            <Button variant="outline" size="sm" onClick={() => window.open(`/api/saml/metadata?cbo_id=${localStorage.getItem('active_cbo_id')}`, '_blank')}>
              <Download className="w-4 h-4 mr-2" /> Download SP Metadata XML
            </Button>
          </CardContent>
        </Card>
      )}

      {/* IdP Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity Provider Settings</CardTitle>
          <CardDescription>Configure your SAML IdP connection</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Label htmlFor="idp-metadata" className="cursor-pointer">
              <Button variant="outline" size="sm" asChild>
                <span><Upload className="w-4 h-4 mr-2" /> Upload IdP Metadata XML</span>
              </Button>
            </Label>
            <input id="idp-metadata" type="file" accept=".xml" className="hidden" onChange={handleUploadMetadata} />
            <span className="text-xs text-muted-foreground">or fill in manually below</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>IdP Entity ID</Label>
              <Input value={idpEntityId} onChange={e => setIdpEntityId(e.target.value)} placeholder="https://idp.example.com/metadata" />
            </div>
            <div>
              <Label>IdP SSO URL</Label>
              <Input value={idpSsoUrl} onChange={e => setIdpSsoUrl(e.target.value)} placeholder="https://idp.example.com/sso" />
            </div>
            <div>
              <Label>IdP SLO URL (optional)</Label>
              <Input value={idpSloUrl} onChange={e => setIdpSloUrl(e.target.value)} placeholder="https://idp.example.com/slo" />
            </div>
          </div>

          <div>
            <Label>IdP Certificate (PEM)</Label>
            <textarea
              className="w-full h-24 px-3 py-2 border rounded-md text-sm font-mono resize-y bg-background"
              value={idpCert}
              onChange={e => setIdpCert(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
            />
          </div>
        </CardContent>
      </Card>

      {/* Attribute Mapping */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Attribute Mapping</CardTitle>
          <CardDescription>Map IdP SAML attributes to WeaveIn user fields</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Email Attribute</Label>
              <Input value={attrEmail} onChange={e => setAttrEmail(e.target.value)} className="font-mono text-sm" />
            </div>
            <div>
              <Label>First Name Attribute</Label>
              <Input value={attrFirstName} onChange={e => setAttrFirstName(e.target.value)} className="font-mono text-sm" />
            </div>
            <div>
              <Label>Last Name Attribute</Label>
              <Input value={attrLastName} onChange={e => setAttrLastName(e.target.value)} className="font-mono text-sm" />
            </div>
            <div>
              <Label>Roles Attribute</Label>
              <Input value={attrRoles} onChange={e => setAttrRoles(e.target.value)} className="font-mono text-sm" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Domains */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Email Domains</CardTitle>
          <CardDescription>Users with these email domains will see the SSO login option</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="lanesla.org" onKeyDown={e => e.key === 'Enter' && addDomain()} />
            <Button variant="outline" size="sm" onClick={addDomain}><Plus className="w-4 h-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {emailDomains.map(d => (
              <Badge key={d} variant="secondary" className="gap-1">
                {d}
                <button onClick={() => removeDomain(d)}><X className="w-3 h-3" /></button>
              </Badge>
            ))}
            {emailDomains.length === 0 && <span className="text-sm text-muted-foreground">No domains configured</span>}
          </div>
        </CardContent>
      </Card>

      {/* Provisioning & Security */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Provisioning & Security</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label>Just-In-Time Provisioning</Label>
              <p className="text-xs text-muted-foreground">Auto-create user accounts from SAML assertions</p>
            </div>
            <Switch checked={jitEnabled} onCheckedChange={setJitEnabled} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Force SSO</Label>
              <p className="text-xs text-muted-foreground">Disable password login for users with SSO-enabled domains</p>
            </div>
            <Switch checked={forceSso} onCheckedChange={setForceSso} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Require Signed Assertions</Label>
              <p className="text-xs text-muted-foreground">Reject unsigned SAML assertions from IdP</p>
            </div>
            <Switch checked={wantAssertionsSigned} onCheckedChange={setWantAssertionsSigned} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label>Require Signed Responses</Label>
              <p className="text-xs text-muted-foreground">Reject unsigned SAML responses from IdP</p>
            </div>
            <Switch checked={wantResponseSigned} onCheckedChange={setWantResponseSigned} />
          </div>
        </CardContent>
      </Card>

      {/* Test Result */}
      {testResult && (
        <Card>
          <CardContent className="pt-6">
            {testResult.checks?.map((c: any, i: number) => (
              <div key={i} className="flex items-center gap-2 py-1">
                {c.passed ? <CheckCircle className="w-4 h-4 text-green-500" /> : <AlertCircle className="w-4 h-4 text-red-500" />}
                <span className="text-sm">{c.check}</span>
                {c.error && <span className="text-xs text-red-500 ml-2">{c.error}</span>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : 'Save Configuration'}
        </Button>
        <Button variant="outline" onClick={handleTest} disabled={testing}>
          {testing ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Testing...</> : 'Test Configuration'}
        </Button>
        {config.enabled && (
          <Button variant="outline" className="text-red-600" onClick={handleDisable}>
            <Trash2 className="w-4 h-4 mr-2" /> Disable SSO
          </Button>
        )}
      </div>
    </div>
  );
}
